import { useState, useEffect, useMemo } from "react";
import React from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { GitCompare, Info, Filter, CheckCircle2, XCircle, ChevronDown, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { readLegislatorCache, writeLegislatorCache } from "@/lib/legislatorCache";
import { ConstituencyCompare } from "@/components/compare/ConstituencyCompare";
import { MoneyCompare } from "@/components/compare/MoneyCompare";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetricLabel = ({ label, description, testId }) => (
  <TooltipProvider>
    <UiTooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help" data-testid={testId}>
          {label}
          <Info className="w-3 h-3 text-gray-400" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-sm">{description}</p>
      </TooltipContent>
    </UiTooltip>
  </TooltipProvider>
);

const CHAMBER_OPTS = [["all", "All"], ["house", "House"], ["senate", "Senate"]];

const ChamberToggle = ({ value, onChange, testIdPrefix }) => (
  <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
    {CHAMBER_OPTS.map(([v, l]) => (
      <button
        key={v}
        type="button"
        data-testid={`${testIdPrefix}-${v}`}
        onClick={() => onChange(v)}
        className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors ${
          value === v ? "bg-orange-500 text-white" : "bg-white text-gray-500 hover:bg-gray-50"
        }`}
      >
        {l}
      </button>
    ))}
  </div>
);

const Compare = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Hydrate instantly from sessionStorage so the Select dropdowns are populated
  // on the very first paint for returning visitors. We still refetch in the
  // background so the list stays fresh.
  const [legislators, setLegislators] = useState(() => readLegislatorCache(null) || readLegislatorCache(119) || []);
  const [legislatorsLoading, setLegislatorsLoading] = useState(legislators.length === 0);
  const [selected1, setSelected1] = useState(searchParams.get("id1") || "");
  const [selected2, setSelected2] = useState(searchParams.get("id2") || "");
  const [chamber1, setChamber1] = useState("all");
  const [chamber2, setChamber2] = useState("all");

  const chamberMatch = (leg, c) =>
    c === "all" ||
    (c === "house"
      ? (leg.chamber || "").includes("House")
      : (leg.chamber || "").includes("Senate"));
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "");

  const options1 = useMemo(
    () => legislators.filter((leg) => leg.id !== selected2 && chamberMatch(leg, chamber1)).sort(byName),
    [legislators, selected2, chamber1]
  );
  const options2 = useMemo(
    () => legislators.filter((leg) => leg.id !== selected1 && chamberMatch(leg, chamber2)).sort(byName),
    [legislators, selected1, chamber2]
  );
  const [topic, setTopic] = useState(searchParams.get("topic") || "");
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedBills, setExpandedBills] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchLegislators();
  }, []);

  useEffect(() => {
    if (selected1 && selected2) {
      compareSelected();
    }
  }, [selected1, selected2, topic]);

  const fetchLegislators = async () => {
    try {
      const response = await axios.get(`${API}/legislators`);
      setLegislators(response.data);
      // Write under both the default and 119 keys so any caller hits a warm cache.
      writeLegislatorCache(null, response.data);
      writeLegislatorCache(119, response.data);
    } catch (error) {
      // Don't show a toast if we already have a cached list, user can still pick.
      if (legislators.length === 0) toast.error("Failed to load legislators");
    } finally {
      setLegislatorsLoading(false);
    }
  };

  const compareSelected = async () => {
    if (!selected1 || !selected2) return;
    try {
      setLoading(true);
      const topicQS = topic ? `&topic=${encodeURIComponent(topic)}` : "";
      const response = await axios.get(`${API}/legislators/compare?ids=${selected1},${selected2}${topicQS}`);
      setComparisonData(response.data);
      const next = { id1: selected1, id2: selected2 };
      if (topic) next.topic = topic;
      setSearchParams(next);
    } catch (error) {
      toast.error("Failed to compare legislators");
    } finally {
      setLoading(false);
    }
  };

  // When two legislators of the SAME party are compared, hard red-vs-blue
  // is misleading (and visually wrong since they're on the same team). We
  // resolve the header banner color per-card using the index plus a
  // same-party check. Different parties → traditional party colors.
  // Same party → light + dark shades of that party's color.
  const sameParty = (() => {
    const legs = comparisonData?.legislators || [];
    return legs.length === 2 && legs[0].party === legs[1].party;
  })();
  const getCardHeaderColor = (party, idx) => {
    if (sameParty) {
      const palette =
        party === "R"
          ? ["bg-red-700", "bg-red-500"]
          : party === "D"
          ? ["bg-blue-700", "bg-blue-500"]
          : party === "I"
          ? ["bg-purple-700", "bg-purple-500"]
          : ["bg-gray-700", "bg-gray-500"];
      return palette[idx] || palette[0];
    }
    const colors = { D: "bg-blue-600", R: "bg-red-600", I: "bg-purple-600" };
    return colors[party] || "bg-gray-600";
  };
  // Chart bar / table accent colors tracked separately so they match the
  // same-party shading logic above. Returns hex codes for Recharts.
  const getAccentHex = (party, idx) => {
    if (sameParty) {
      const palette =
        party === "R"
          ? ["#991b1b", "#ef4444"]
          : party === "D"
          ? ["#1e40af", "#3b82f6"]
          : ["#6d28d9", "#a855f7"];
      return palette[idx] || palette[0];
    }
    return party === "R" ? "#dc2626" : party === "D" ? "#2563eb" : "#9333ea";
  };

  const descriptions = comparisonData?.metricDescriptions || {};

  const prepareChartData = () => {
    if (!comparisonData || comparisonData.legislators.length < 2) return [];
    const [leg1, leg2] = comparisonData.legislators;
    return [
      { metric: "Bills Sponsored", [leg1.name]: leg1.stats.billsSponsored, [leg2.name]: leg2.stats.billsSponsored },
      { metric: "Bills Enacted", [leg1.name]: leg1.stats.billsPassed, [leg2.name]: leg2.stats.billsPassed },
      {
        metric: "Voting %",
        [leg1.name]: Number(((leg1.stats.votesCast / leg1.stats.votesCount) * 100).toFixed(1)),
        [leg2.name]: Number(((leg2.stats.votesCast / leg2.stats.votesCount) * 100).toFixed(1)),
      },
      { metric: "Bipartisan Index", [leg1.name]: leg1.stats.bipartisanIndex, [leg2.name]: leg2.stats.bipartisanIndex },
    ];
  };

  const voteBadge = (v) => {
    const c =
      v === "Yea" || v === "Aye" || v === "Yes"
        ? "bg-green-100 text-green-800 border-green-200"
        : v === "Nay" || v === "No"
        ? "bg-red-100 text-red-800 border-red-200"
        : "bg-gray-100 text-gray-800 border-gray-200";
    return <Badge className={`${c} border`}>{v}</Badge>;
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />

      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <GitCompare className="w-10 h-10 text-orange-400" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900" data-testid="compare-title">
                Compare Legislators
              </h1>
              <p className="text-base text-gray-600 mt-1">
                Side-by-side career stats + real roll-call agreement, filterable by policy area
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selection */}
        <Card className="p-6 mb-8 bg-white rounded-xl border-2 border-gray-200">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Legislator 1</label>
                <ChamberToggle value={chamber1} onChange={setChamber1} testIdPrefix="chamber-filter-1" />
              </div>
              <Select value={selected1} onValueChange={setSelected1} disabled={legislatorsLoading && legislators.length === 0}>
                <SelectTrigger data-testid="select-legislator-1" className="w-full">
                  <SelectValue
                    placeholder={
                      legislatorsLoading && legislators.length === 0
                        ? "Loading legislators…"
                        : "Select first legislator"
                    }
                  />
                  {legislatorsLoading && legislators.length === 0 && (
                    <span
                      aria-hidden
                      className="ml-2 inline-block w-3.5 h-3.5 rounded-full border-2 border-orange-400 border-t-transparent animate-spin"
                    />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {options1.map((leg) => (
                    <SelectItem key={leg.id} value={leg.id}>
                      {leg.name} ({leg.party}-{leg.state})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Legislator 2</label>
                <ChamberToggle value={chamber2} onChange={setChamber2} testIdPrefix="chamber-filter-2" />
              </div>
              <Select value={selected2} onValueChange={setSelected2} disabled={legislatorsLoading && legislators.length === 0}>
                <SelectTrigger data-testid="select-legislator-2" className="w-full">
                  <SelectValue
                    placeholder={
                      legislatorsLoading && legislators.length === 0
                        ? "Loading legislators…"
                        : "Select second legislator"
                    }
                  />
                  {legislatorsLoading && legislators.length === 0 && (
                    <span
                      aria-hidden
                      className="ml-2 inline-block w-3.5 h-3.5 rounded-full border-2 border-orange-400 border-t-transparent animate-spin"
                    />
                  )}
                </SelectTrigger>
                <SelectContent>
                  {options2.map((leg) => (
                    <SelectItem key={leg.id} value={leg.id}>
                      {leg.name} ({leg.party}-{leg.state})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Filter className="w-4 h-4" /> Compare by Topic
              </label>
              <Select value={topic || "__all__"} onValueChange={(v) => setTopic(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="select-topic" className="w-full">
                  <SelectValue placeholder="All topics" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All topics</SelectItem>
                  {(comparisonData?.availableTopics || []).map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {comparisonData && (
                <p className="text-xs text-gray-500 mt-1">
                  {comparisonData.availableTopics?.length || 0} topics detected in their shared roll-calls
                </p>
              )}
            </div>
          </div>
        </Card>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Comparing legislators...</p>
          </div>
        )}

        {!loading && comparisonData && comparisonData.legislators.length >= 2 && (
          <div>
            {/* Profile Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {comparisonData.legislators.map((leg, idx) => (
                <Link key={leg.id} to={`/legislator/${leg.id}`} className="block">
                <Card
                  className="overflow-hidden border-2 border-gray-200 rounded-xl cursor-pointer hover:shadow-xl transition-all h-full"
                  data-testid={`compare-profile-${leg.id}`}
                >
                  <div className={`${getCardHeaderColor(leg.party, idx)} p-6 text-white`}>
                    <div className="flex items-center gap-4">
                      <img src={leg.photo} alt={leg.name} className="w-24 h-24 rounded-xl object-cover border-4 border-white shadow-lg" />
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold mb-1">{leg.name}</h2>
                        <p className="text-base opacity-90">{leg.title}</p>
                        <p className="text-sm opacity-80">{leg.state} • {leg.chamber}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-blue-600">{leg.stats.billsSponsored}</p>
                        <p className="text-xs text-gray-600">
                          <MetricLabel label="Bills Sponsored" description={descriptions.billsSponsored || ""} testId={`tip-sponsored-${leg.id}`} />
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-green-600">{leg.stats.billsPassed}</p>
                        <p className="text-xs text-gray-600">
                          <MetricLabel label="Bills Enacted" description={descriptions.billsPassed || ""} testId={`tip-passed-${leg.id}`} />
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-purple-600">
                          {((leg.stats.votesCast / leg.stats.votesCount) * 100).toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-600">
                          <MetricLabel label="Voting Record" description={descriptions.votingRecord || ""} testId={`tip-voting-${leg.id}`} />
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-orange-600">{leg.stats.bipartisanIndex.toFixed(1)}</p>
                        <p className="text-xs text-gray-600">
                          <MetricLabel label="Bipartisan Index" description={descriptions.bipartisanIndex || ""} testId={`tip-bipartisan-${leg.id}`} />
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
                </Link>
              ))}
            </div>

            {/* Agreement summary */}
            {comparisonData.agreementPercent !== undefined && (
              <Card
                className="p-6 mb-8 border border-orange-500/30 rounded-xl"
                style={{ background: "linear-gradient(135deg, rgba(255,138,0,0.08), rgba(11,11,15,0.95))" }}
                data-testid="agreement-card"
              >
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="text-center md:text-left">
                    <p className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-1">
                      {topic ? `Agreement on ${topic}` : "Overall Agreement"}
                    </p>
                    <p className="text-6xl font-bold text-blue-600">{comparisonData.agreementPercent}%</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {comparisonData.agreementVotes} of {comparisonData.commonVotes} roll-call votes matched
                    </p>
                  </div>
                  <div className="text-sm text-gray-700 max-w-md md:text-right">
                    <p className="font-medium text-gray-900 mb-1">What this means</p>
                    <p>
                      Among the roll-call votes where <strong>both</strong>{" "}
                      {comparisonData.legislators[0].name.split(",")[0]} and{" "}
                      {comparisonData.legislators[1].name.split(",")[0]} participated
                      {topic ? ` on "${topic}" bills` : ""}, this is the share where they voted the <em>same</em> way.
                      Based only on bills currently cached on the server, scroll down to see every bill counted.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <div className="grid gap-6 xl:grid-cols-2 items-start" data-testid="compare-context-grid">
              <ConstituencyCompare legislators={comparisonData.legislators} />
              <MoneyCompare legislators={comparisonData.legislators} />
            </div>

            {/* Topic breakdown */}
            {comparisonData.topicBreakdown && comparisonData.topicBreakdown.length > 0 && (
              <Card className="p-6 mb-8 bg-white rounded-xl border-2 border-gray-200" data-testid="topic-breakdown-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Agreement by Policy Area</h2>
                    <p className="text-sm text-gray-600 mt-1">How often they agree on bills grouped by Congress-assigned policy area.</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={Math.max(160, comparisonData.topicBreakdown.length * 50)}>
                  <BarChart data={comparisonData.topicBreakdown} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" domain={[0, 100]} unit="%" stroke="#6b7280" />
                    <YAxis type="category" dataKey="topic" stroke="#6b7280" width={220} tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: "8px" }}
                      formatter={(value, name, payload) => {
                        const p = payload?.payload;
                        return [`${value}% (${p?.agreementVotes}/${p?.commonVotes} votes)`, "Agreement"];
                      }}
                    />
                    <Bar dataKey="agreementPercent" fill="#3b82f6" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Career Performance Comparison */}
            <Card className="p-6 mb-8 bg-white rounded-xl border-2 border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Career Performance Comparison</h2>
              <p className="text-sm text-gray-600 mb-6">
                These are <strong>career totals</strong> across <em>all</em> bills they've sponsored/voted on, they do NOT change with the topic filter above.
              </p>
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={prepareChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="metric" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip contentStyle={{ borderRadius: "8px" }} />
                  <Legend />
                  <Bar dataKey={comparisonData.legislators[0]?.name} fill={getAccentHex(comparisonData.legislators[0]?.party, 0)} radius={[8, 8, 0, 0]} />
                  <Bar dataKey={comparisonData.legislators[1]?.name} fill={getAccentHex(comparisonData.legislators[1]?.party, 1)} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Common bills detail table */}
            {comparisonData.commonBills && comparisonData.commonBills.length > 0 && (() => {
              // Group by billId so multi-roll bills appear once with a
              // "N roll calls" badge and expandable per-roll detail.
              const [leg0, leg1] = comparisonData.legislators;
              const groups = {};
              comparisonData.commonBills.forEach((b) => {
                const k = b.billId;
                if (!groups[k]) {
                  groups[k] = {
                    billId: k,
                    billTitle: b.billTitle,
                    subjects: b.subjects,
                    rolls: [],
                  };
                }
                groups[k].rolls.push(b);
              });
              const grouped = Object.values(groups).map((g) => {
                const l0Votes = g.rolls.map((r) => r[leg0.id]).filter(Boolean);
                const l1Votes = g.rolls.map((r) => r[leg1.id]).filter(Boolean);
                const l0Uniform = new Set(l0Votes).size === 1 ? l0Votes[0] : "Mixed";
                const l1Uniform = new Set(l1Votes).size === 1 ? l1Votes[0] : "Mixed";
                const agrees = g.rolls.filter((r) => r.agree).length;
                const latest = g.rolls.reduce((a, r) => (r.date > (a?.date || "") ? r : a), null);
                return { ...g, l0Vote: l0Uniform, l1Vote: l1Uniform, agrees, total: g.rolls.length, latestDate: latest?.date };
              });
              grouped.sort((a, b) => (b.latestDate || "").localeCompare(a.latestDate || ""));
              return (
                <Card className="p-6 mb-8 bg-white rounded-xl border-2 border-gray-200" data-testid="common-bills-card">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Every Shared Bill</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    {grouped.length} unique bills with {comparisonData.commonVotes} total roll-call votes. Click a row to open the bill;
                    click the chevron to see every individual roll call on that bill.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-gray-100 border-b-2 border-gray-300">
                          <th className="text-left p-3 font-semibold text-gray-700 w-8"></th>
                          <th className="text-left p-3 font-semibold text-gray-700">Latest Vote</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Bill</th>
                          <th className="text-left p-3 font-semibold text-gray-700">Policy Area</th>
                          <th className="text-center p-3 font-semibold text-gray-700">
                            {leg0.name.split(",")[0]}
                          </th>
                          <th className="text-center p-3 font-semibold text-gray-700">
                            {leg1.name.split(",")[0]}
                          </th>
                          <th className="text-center p-3 font-semibold text-gray-700">Agree?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grouped.map((g, idx) => {
                          const isOpen = expandedBills[g.billId];
                          return (
                            <React.Fragment key={g.billId}>
                              <tr
                                className="border-b border-gray-200 hover:bg-blue-50 cursor-pointer"
                                data-testid={`common-bill-row-${idx}`}
                              >
                                <td className="p-3">
                                  {g.total > 1 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedBills((p) => ({ ...p, [g.billId]: !p[g.billId] }));
                                      }}
                                      className="text-gray-500 hover:text-gray-900"
                                      data-testid={`expand-${g.billId}`}
                                    >
                                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                  )}
                                </td>
                                <td className="p-3 text-gray-600" onClick={() => navigate(`/bill/${g.billId}`)}>
                                  {g.latestDate && new Date(g.latestDate).toLocaleDateString()}
                                </td>
                                <td className="p-3">
                                  <Link to={`/bill/${g.billId}`} className="text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                                    {g.billId}
                                  </Link>
                                  <div className="text-xs text-gray-500 line-clamp-1 max-w-sm">{g.billTitle}</div>
                                  {g.total > 1 && (
                                    <Badge variant="outline" className="mt-1 text-xs">
                                      {g.total} roll calls
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  <div className="flex flex-wrap gap-1">
                                    {(g.subjects || []).slice(0, 2).map((s, i) => (
                                      <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                                    ))}
                                  </div>
                                </td>
                                <td className="p-3 text-center">{voteBadge(g.l0Vote)}</td>
                                <td className="p-3 text-center">{voteBadge(g.l1Vote)}</td>
                                <td className="p-3 text-center">
                                  {g.agrees === g.total ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-600 inline" />
                                  ) : g.agrees === 0 ? (
                                    <XCircle className="w-5 h-5 text-red-500 inline" />
                                  ) : (
                                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                                      {g.agrees}/{g.total}
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                              {isOpen && g.rolls.map((r, ri) => (
                                <tr key={`${g.billId}-${ri}`} className="bg-gray-50 border-b border-gray-200 text-xs">
                                  <td></td>
                                  <td className="p-2 pl-6 text-gray-500">{r.date && new Date(r.date).toLocaleDateString()}</td>
                                  <td className="p-2 text-gray-600 max-w-md line-clamp-1">{r.chamber} • {r.description}</td>
                                  <td></td>
                                  <td className="p-2 text-center">{voteBadge(r[leg0.id])}</td>
                                  <td className="p-2 text-center">{voteBadge(r[leg1.id])}</td>
                                  <td className="p-2 text-center">
                                    {r.agree ? <CheckCircle2 className="w-4 h-4 text-green-600 inline" /> : <XCircle className="w-4 h-4 text-red-500 inline" />}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              );
            })()}

            {comparisonData.commonVotes === 0 && (
              <Card className="p-6 bg-yellow-50 border-2 border-yellow-200 text-yellow-900 rounded-xl">
                <p className="font-semibold">No shared roll-call votes found in the cache{topic ? ` for "${topic}"` : ""}.</p>
                <p className="text-sm mt-1">
                  Try clearing the topic filter, or browse a few bills first to populate the cache, agreement is computed only over bills
                  that have been loaded on the server.
                </p>
              </Card>
            )}
          </div>
        )}

        {!loading && (!comparisonData || comparisonData.legislators.length < 2) && selected1 && selected2 && (
          <div className="text-center py-12">
            <GitCompare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-600">Unable to load comparison data</p>
          </div>
        )}

        {(!selected1 || !selected2) && (
          <div className="text-center py-12">
            <GitCompare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-600">Select two legislators to compare</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Compare;
